import { ROUND2_DEFAULT_FEMALES, ROUND2_DEFAULT_MALES, type GenderCounts } from './round2';
import { filterByGender } from './scoreboard-view';
import type { LeaderboardRow, ShooterGender } from './shooting';

/** Round 2 cut counts. PR #50 asks for these when moving shooters and does not store them. */
export const BIG_SCREEN_DEFAULT_QUALIFIERS: GenderCounts = {
  male: ROUND2_DEFAULT_MALES,
  female: ROUND2_DEFAULT_FEMALES,
};

/** Name rows that fit under the header on a 1920×1080 stage, matching the approved mock. */
export const BIG_SCREEN_VISIBLE_ROWS = 15;
/** Ranks that stay on screen while the rest of a long list pages. */
export const BIG_SCREEN_PINNED = 3;
export const BIG_SCREEN_ROW_PX = 47;
export const BIG_SCREEN_CUT_PX = 30;
/** How long each page of a long list stays put before the next one. */
export const BIG_SCREEN_PAGE_MS = 6000;
/** Backup refresh so a TV picks up a theme change even if the live socket misses it. */
export const BIG_SCREEN_POLL_MS = 10_000;

export type BigScreenTheme = 'dark' | 'light';

export function parseBigScreenTheme(value: unknown): BigScreenTheme | null {
  return value === 'light' || value === 'dark' ? value : null;
}

export function bigScreenThemeOrDark(value: unknown): BigScreenTheme {
  return value === 'light' ? 'light' : 'dark';
}

export function bigScreenPath(slug: string): string {
  return `/scoreboard/${encodeURIComponent(slug)}/display`;
}

/** Chennai local time. The board is for a Chennai event, so the clock does not follow the TV's time zone. */
export function chennaiClock(date = new Date()): string {
  return date.toLocaleTimeString('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export interface BigScreenShooter {
  shooterId: string;
  /** Rank within this gender. Ties share a number. */
  rank: number;
  /** 1-based place in the gender list. The cut line uses this, so a tie on the boundary still cuts after N people. */
  position: number;
  bib: string | null;
  name: string;
  teamName?: string | null;
  score: string;
  /** First three places, drawn in the pinned red rows. */
  top: boolean;
}

export type BigScreenLine =
  | { kind: 'shooter'; shooter: BigScreenShooter; cutAfter: boolean }
  | { kind: 'cut' };

export interface PresentedColumn {
  scrolling: boolean;
  note: { variant: 'scroll' | 'plain'; text: string } | null;
  pinned: BigScreenLine[];
  rest: BigScreenLine[];
  windowPx: number;
  /** Translate offsets for `rest`. A single 0 means the list does not move. */
  offsets: number[];
}

export interface BigScreenColumns {
  male: BigScreenShooter[];
  female: BigScreenShooter[];
  /** Everyone with no Male / Female tag, scored or not. They are not on either list. */
  missingGender: number;
}

/**
 * Men ranked among men and women among women, using the same order and tie-break as the public board.
 * Shooters with no gender are counted and left off. Shooters with no score (rank 0) are left off too.
 */
export function bigScreenColumns(rows: readonly LeaderboardRow[], counts: GenderCounts): BigScreenColumns {
  return {
    missingGender: rows.filter((row) => row.gender == null).length,
    male: toShooters(filterByGender([...rows], 'male')),
    female: toShooters(filterByGender([...rows], 'female')),
  };
}

function toShooters(rows: readonly LeaderboardRow[]): BigScreenShooter[] {
  return rows
    .filter((row) => row.rank > 0)
    .map((row, index) => ({
      shooterId: row.shooterId,
      rank: row.rank,
      position: index + 1,
      bib: row.bib,
      name: row.name,
      teamName: row.teamName ?? null,
      score: row.totalDisplay,
      top: index < BIG_SCREEN_PINNED,
    }));
}

export function columnNote(cut: number, scrolling: boolean): PresentedColumn['note'] {
  if (cut <= 0) return null;
  if (scrolling) return { variant: 'scroll', text: `Ranks 1–${cut} · auto-scrolling` };
  return { variant: 'plain', text: `Top ${cut} → Round 2` };
}

/** One gender column: a cut line after the Nth shooter, and paging only when the names do not fit. */
export function presentColumn(rows: readonly BigScreenShooter[], cut: number): PresentedColumn {
  const lines = linesFor(rows, cut);
  const scrolling = rows.length > BIG_SCREEN_VISIBLE_ROWS;
  const note = columnNote(cut, scrolling);
  if (!scrolling) return { scrolling, note, pinned: [], rest: lines, windowPx: 0, offsets: [0] };
  const { pinned, rest } = splitPinned(lines, BIG_SCREEN_PINNED);
  const windowPx = (BIG_SCREEN_VISIBLE_ROWS - BIG_SCREEN_PINNED) * BIG_SCREEN_ROW_PX;
  return { scrolling, note, pinned, rest, windowPx, offsets: scrollOffsets(rest, windowPx) };
}

function linesFor(rows: readonly BigScreenShooter[], cut: number): BigScreenLine[] {
  const lines: BigScreenLine[] = [];
  rows.forEach((shooter, index) => {
    const cutAfter = cut > 0 && index + 1 === cut;
    lines.push({ kind: 'shooter', shooter, cutAfter });
    if (cutAfter) lines.push({ kind: 'cut' });
  });
  return lines;
}

/** Keep the first `count` shooters pinned, including a cut line that sits on the last of them. */
export function splitPinned(lines: readonly BigScreenLine[], count: number): { pinned: BigScreenLine[]; rest: BigScreenLine[] } {
  if (count <= 0) return { pinned: [], rest: [...lines] };
  let seen = 0;
  let index = 0;
  for (; index < lines.length; index += 1) {
    if (lines[index]!.kind === 'shooter') seen += 1;
    if (seen >= count) {
      index += 1;
      if (lines[index]?.kind === 'cut') index += 1;
      break;
    }
  }
  return { pinned: lines.slice(0, index), rest: lines.slice(index) };
}

/**
 * Page offsets for the unpinned rows. The last offset lands on a row boundary such that the
 * bottom of the list (the cut line, when it is in this section) sits inside the window.
 */
export function scrollOffsets(lines: readonly BigScreenLine[], windowPx: number): number[] {
  if (windowPx <= 0 || lines.length === 0) return [0];
  const tops: number[] = [];
  let y = 0;
  for (const line of lines) {
    tops.push(y);
    y += line.kind === 'cut' ? BIG_SCREEN_CUT_PX : BIG_SCREEN_ROW_PX;
  }
  if (y <= windowPx) return [0];
  const need = y - windowPx;
  let maxOff = tops[tops.length - 1] ?? 0;
  for (const top of tops) {
    if (top >= need) {
      maxOff = top;
      break;
    }
  }
  const offsets = [0];
  let pos = 0;
  while (pos < maxOff && offsets.length < 40) {
    const target = Math.min(maxOff, pos + windowPx);
    let next = 0;
    for (const top of tops) {
      if (top > pos && top <= target) next = top;
    }
    if (next <= pos) next = maxOff;
    offsets.push(next);
    pos = next;
  }
  return offsets;
}

export function qualifierCountsFor(
  bigScreen: { qualifiers?: { male?: number; female?: number } } | null | undefined,
): GenderCounts {
  const male = bigScreen?.qualifiers?.male;
  const female = bigScreen?.qualifiers?.female;
  return {
    male: typeof male === 'number' && male >= 0 ? male : BIG_SCREEN_DEFAULT_QUALIFIERS.male,
    female: typeof female === 'number' && female >= 0 ? female : BIG_SCREEN_DEFAULT_QUALIFIERS.female,
  };
}

export function genderHeading(gender: ShooterGender): string {
  return gender === 'male' ? 'Men' : 'Women';
}
